import type { AllocationBE, AssetTransactionTypeEnum, IAssetTransaction } from '@talos/kyoko';
import { baseRestApi, createTag } from '../../../utils/rtkQuery/baseRestApi';

export interface CreateAssetTransactionItem {
  TransactionID: string;
  TransactTime: string;
  Type: AssetTransactionTypeEnum;
  MarketAccount: string;
  Asset: string;
  Amount: string;
  PositionAsset?: string;
  SubAccount?: string;
  Allocation?: AllocationBE;
  AllocationStatus?: 'Manual' | 'Unallocated';
  Comment?: string;
  Price?: string;
  PriceCurrency?: string;
}

/**
 * Fields the backend accepts inside PATCH `/asset-transactions/{tid}` `Patch`.
 * Patch type (AssetTransaction4521) sent to backend is defined in the types
 * file as unknown, and these fields are then resolved by the backend. A
 * future backend ticket could make this more maintainable by adding an
 * explicit type.
 */
export type AssetTransactionPatchFields = Partial<
  Pick<
    IAssetTransaction,
    | 'Allocation'
    | 'Amount'
    | 'Comment'
    | 'Asset'
    | 'PositionAsset'
    | 'Price'
    | 'PriceCurrency'
    | 'Rate'
    | 'SubAccount'
    | 'TransactTime'
    | 'Type'
  >
>;

export interface UpdateAssetTransactionRequest {
  Revision: number;
  Patch: AssetTransactionPatchFields;
}

const assetTransactionsApiInternal = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    createAssetTransaction: builder.mutation<IAssetTransaction[], CreateAssetTransactionItem[]>({
      query: body => ({
        method: 'POST',
        url: '/asset-transactions',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: () => [createTag('AssetTransaction', 'LIST')],
    }),
    updateAssetTransaction: builder.mutation<IAssetTransaction[], { tid: string; body: UpdateAssetTransactionRequest }>(
      {
        query: ({ tid, body }) => ({
          method: 'PATCH',
          url: `/asset-transactions/${tid}`,
          body,
          headers: { 'Content-Type': 'application/json' },
        }),
        invalidatesTags: () => [createTag('AssetTransaction', 'LIST')],
      }
    ),
  }),
});

export const { useCreateAssetTransactionMutation, useUpdateAssetTransactionMutation } = assetTransactionsApiInternal;
